import { packageUpgradeJourney } from '@/Analytics/Segment';
import useSimpleModal from '@/Components/Modal/useSimpleModal';
import getLabel from '@/Utils/LabelsConfig';
import { useMutation } from '@apollo/client';
import * as SentryBrowser from '@sentry/browser';
import { AppId } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import sessionId from '@/Utils/SessionId';
import usePersistCallback from '../usePersistCallback';
import ADD_CUSTOMER_PACKAGE from './addCustomerPackage';
import {
  LABELS_CONFIG,
  PACKAGE_UPGRADE_EVENTS,
  PKG_SUBSCRIBE_EVENT,
  PURCHASE_FAILURE_REASONS,
} from './constants';

type OfferProps = {
  offerId: string;
  packageName: string;
  packagePrice: string;
};
type OriginProps = {
  origin: string;
};
type SuccessCallback = () => void;
type CustomerPurchaseCallback = () => void;
type PurchaseEvent = typeof PKG_SUBSCRIBE_EVENT[keyof typeof PKG_SUBSCRIBE_EVENT];
interface PurchaseEventsProps {
  responseType: PurchaseEvent;
  openModal: (message: any) => void;
  offerDetails: OfferProps;
  origin: OriginProps;
}

const showApiErrorPopup = (
  openModal: (msg: any) => void,
  errorMessage: string,
  offerDetails: OfferProps,
  origin: OriginProps,
  failureReason: string,
) => {
  packageUpgradeJourney({
    action: PACKAGE_UPGRADE_EVENTS.PACKAGE_UPGRRADE_FAILED,
    origin,
    reason: failureReason,
    offer_id: offerDetails.offerId,
    offer_title: offerDetails.packageName,
    price: offerDetails.packagePrice,
  });
  openModal({
    title: getLabel(LABELS_CONFIG.PURCHASE_UNSUCCESSFUL),
    content: errorMessage,
    onOk: () => {},
  });
};
const purchaseEvents = (
  { responseType, openModal, offerDetails, origin }: PurchaseEventsProps,
  handlePurchaseEvent?: (event: PurchaseEvent) => void,
) => {
  const eventMap: Record<
    PurchaseEvent,
    { labelKey: keyof typeof LABELS_CONFIG; reason: string }
  > = {
    [PKG_SUBSCRIBE_EVENT.INVALID_CUSTOMER_ACCOUNT_FOR_PURCHASE]: {
      labelKey: 'INVALID_CUSTOMER_ACCOUNT_FOR_PURCHASE',
      reason: PURCHASE_FAILURE_REASONS.INVALID_CUSTOMER_ACCOUNT,
    },
    [PKG_SUBSCRIBE_EVENT.CUSTOMER_ACCOUNT_IN_ARREARS_ERROR]: {
      labelKey: 'ACCOUNT_IN_ARREARS',
      reason: PURCHASE_FAILURE_REASONS.CUSTOMER_ACCOUNT_IN_ARREARS,
    },
    [PKG_SUBSCRIBE_EVENT.ADD_CUSTOMER_PKG_DUPLICATE_RESPONSE]: {
      labelKey: 'DUPLICATE_PURCHASE',
      reason: PURCHASE_FAILURE_REASONS.DUPLICATE_CONTENT_PURCHASE,
    },
    [PKG_SUBSCRIBE_EVENT.ADD_CUSTOMER_PKG_REJECTED_RESPONSE]: {
      labelKey: 'PACKAGE_REJECTED',
      reason: PURCHASE_FAILURE_REASONS.CUSTOMER_PURCHASE_REJECTED,
    },
  };

  const matchedEvent = eventMap[responseType];

  if (matchedEvent) {
    showApiErrorPopup(
      openModal,
      getLabel(LABELS_CONFIG[matchedEvent.labelKey]),
      offerDetails,
      origin,
      matchedEvent.reason,
    );
  } else {
    showApiErrorPopup(
      openModal,
      getLabel(LABELS_CONFIG.PKG_PURCHASE_FAILER_MSG),
      offerDetails,
      origin,
      PURCHASE_FAILURE_REASONS.GENERAL_PURCHASE_FAILURE,
    );
  }

  handlePurchaseEvent?.(responseType);
};

const useAddCustomerPackage = () => {
  const [addCustomerPackageQuery, { loading, error }] = useMutation(ADD_CUSTOMER_PACKAGE);
  const { openModal } = useSimpleModal();

  const handleSuccess = (response, handleSuccessEvent, offerDetails, origin) => {
    const responseType = response?.__typename;

    if (responseType === PKG_SUBSCRIBE_EVENT.ADD_CUSTOMER_PKG_SUCCESS_RESPONSE) {
      packageUpgradeJourney({
        action: PACKAGE_UPGRADE_EVENTS.PACKAGE_UPGRRADE_COMPLETED,
        origin,
        offer_id: offerDetails.offerId,
        offer_title: offerDetails.packageName,
        price: offerDetails.packagePrice,
      });
      return handleSuccessEvent?.(PKG_SUBSCRIBE_EVENT.ADD_CUSTOMER_PKG_SUCCESS_RESPONSE);
    }
    return purchaseEvents({ responseType, openModal, offerDetails, origin }, handleSuccessEvent);
  };

  const handleFailure = (err: any, offerDetails: OfferProps, origin: OriginProps) => {
    showApiErrorPopup(
      openModal,
      getLabel(LABELS_CONFIG.PKG_PURCHASE_FAILER_MSG),
      offerDetails,
      origin,
      PURCHASE_FAILURE_REASONS.GENERAL_PURCHASE_FAILURE,
    );
    SentryBrowser.captureException(err);
  };

  const addCustomerPackage = usePersistCallback(
    (
      offerDetails: OfferProps,
      origin: OriginProps,
      handleSuccessEvent?: SuccessCallback,
      handlePurchaseEvent?: CustomerPurchaseCallback,
    ) => {
      addCustomerPackageQuery({
        variables: {
          appId: AppId.SkyGoWeb,
          offerId: offerDetails.offerId,
          deviceId: sessionId,
        },
      })
        .then(response => {
          const data = response?.data?.addCustomerPackage;
          if (data) {
            handleSuccess(data, handleSuccessEvent, offerDetails, origin);
          }
        })
        .catch(err => {
          packageUpgradeJourney({
            action: PACKAGE_UPGRADE_EVENTS.PACKAGE_UPGRRADE_FAILED,
            origin,
            reason: err,
            offer_id: offerDetails.offerId,
            offer_title: offerDetails.packageName,
            price: offerDetails.packagePrice,
          });
          if (err.message?.includes('Failed to fetch')) {
            openModal({
              title: getLabel(LABELS_CONFIG.SOMETHING_NOT_RIGHT),
              content: getLabel(LABELS_CONFIG.NO_INTERNET_CONNECTION),
              okText: 'Refresh',
              onOk: () => {
                window.location.reload();
              },
            });
          } else {
            handleFailure(err, offerDetails, origin);
          }
          handlePurchaseEvent?.();
        });
    },
  );

  return { loading, error, addCustomerPackage };
};

export default useAddCustomerPackage;
